/**
* @license Apache-2.0
*
* Copyright (c) 2019 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isPlainObject from '@stdlib/assert-is-plain-object';
import hasOwnProp from '@stdlib/assert-has-own-property';
import { isPrimitive as isInteger } from '@stdlib/assert-is-integer';
import { isPrimitive as isPositiveInteger } from '@stdlib/assert-is-positive-integer';
import { isPrimitive as isNonNegativeInteger } from '@stdlib/assert-is-nonnegative-integer';
import { isPrimitive as isNonNegativeNumber } from '@stdlib/assert-is-nonnegative-number';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Validates function options.
*
* @private
* @param {Object} opts - destination object
* @param {Options} options - function options
* @param {PositiveInteger} [options.period] - number of iterations before a sine wave repeats
* @param {NonNegativeNumber} [options.amplitude] - peak amplitude
* @param {integer} [options.offset] - phase offset (in units of iterations)
* @param {NonNegativeInteger} [options.iter] - number of iterations
* @returns {(Error|null)} null or an error object
*
* @example
* var opts = {};
* var options = {
*     'iter': 100
* };
* var err = validate( opts, options );
* if ( err ) {
*     throw err;
* }
*/
function validate( opts, options ) {
	if ( !isPlainObject( options ) ) {
		return new TypeError( format( '0tX2V', options ) );
	}
	if ( hasOwnProp( options, 'period' ) ) {
		opts.period = options.period;
		if ( !isPositiveInteger( options.period ) ) {
			return new TypeError( format( '0tX8N', 'period', options.period ) );
		}
	}
	if ( hasOwnProp( options, 'amplitude' ) ) {
		opts.amplitude = options.amplitude;
		if ( !isNonNegativeNumber( options.amplitude ) ) {
			return new TypeError( format( '0tX4k', 'amplitude', options.amplitude ) );
		}
	}
	if ( hasOwnProp( options, 'offset' ) ) {
		opts.offset = options.offset;
		if ( !isInteger( options.offset ) ) {
			return new TypeError( format( '0tX8M', 'offset', options.offset ) );
		}
	}
	if ( hasOwnProp( options, 'iter' ) ) {
		opts.iter = options.iter;
		if ( !isNonNegativeInteger( options.iter ) ) {
			return new TypeError( format( '0tX2t', 'iter', options.iter ) );
		}
	}
	return null;
}


// EXPORTS //

export default validate;
